// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
import { escapeHtml } from './utils/format';

export interface GlossaryEntry {
  title: string;
  body: string;
}

export const GLOSSARY: Record<string, GlossaryEntry> = {
  incident: {
    title: 'Shark incident',
    body: 'A recorded encounter in which a shark bit, or attempted to bite, a person or a boat. The database uses the neutral term "incident" (not "attack") because many bites are minor, defensive, or provoked.',
  },
  provoked: {
    title: 'Provoked vs unprovoked',
    body: 'A bite is "provoked" when the person initiated contact — spearfishing, fishing, feeding, handling or otherwise interacting with the shark. "Unprovoked" bites happen without human provocation. About a third of records here are provoked.',
  },
  fatal: {
    title: 'Outcome: fatal / injured / uninjured',
    body: 'Every incident is classed by what happened to the person: fatal (the bite caused death), injured (bitten and hurt but survived), or uninjured (a bite or bump that broke no skin, e.g. a shark striking a surfboard).',
  },
  'fatality-rate': {
    title: 'Fatality rate',
    body: 'The share of incidents with a known outcome that ended in death. It has fallen from about a third before 1940 to roughly one in ten since 2000 — thanks to faster rescue, better first aid and trauma medicine, and fuller reporting of minor bites.',
  },
  'big-three': {
    title: 'The three species',
    body: 'White, tiger and bull sharks are large enough to cause life-threatening injuries and account for almost every fatal bite in the record. Most other species cause only minor wounds.',
  },
  'board-riding': {
    title: 'Board-riding',
    body: 'Surfing, bodyboarding and similar board activities. The source database groups them under "boarding". Board-riders are bitten often but survive far more often than swimmers.',
  },
  unprovoked: {
    title: 'Unprovoked bite',
    body: 'A bite that occurred without the person deliberately interacting with the shark — the case most people picture. Compare with a "provoked" bite during fishing, feeding or handling.',
  },
  reconciliation: {
    title: 'How the data is checked',
    body: 'The build cross-checks two independently recorded fields: it confirms that ≥98% of geolocated incidents fall inside Australia, and that each incident’s state matches its coordinates for ≥90% of records. Agreement proves the columns were read correctly.',
  },
  asid: {
    title: 'Australian Shark-Incident Database',
    body: 'The national record maintained by Taronga Conservation Society Australia with Flinders University and NSW DPI, compiled from victim and witness questionnaires, fisheries reports and media since 1791. Formerly the Australian Shark-Attack File.',
  },
};

/** Wrap a phrase with a click-to-explain glossary trigger + info icon. */
export function glossaryTerm(term: string, text: string): string {
  const has = !!GLOSSARY[term];
  const safe = escapeHtml(text);
  if (!has) return safe;
  return `<span class="glossary-link" data-term="${escapeHtml(term)}" tabindex="0" role="button" aria-label="${safe} — what does this mean?">${safe}</span><button class="info-icon" data-term="${escapeHtml(term)}" aria-label="About: ${safe}" tabindex="-1">i</button>`;
}
